import { env } from "@/env";
import axios from "axios";
import { ZodType } from "zod";
import { auth } from "./auth";
import { getUserDb, invalidateByEntities } from './db';
import {secureEncrypt, secureDecrypt} from './crypto';

export type paths =
  | "/api/services"
  | "/api/services/$id"
  | "/api/providers"
  | "/api/providers/$id"
  | "/api/guides"
  | "/api/guides/$id"
  | "/api/workers"
  | "/api/workers/$id"
  | "/api/agencies"
  | "/api/agencies/$id"
  | "/api/tours"
  | "/api/tours/$id"
  | "/api/regions"
  | "/api/regions/$id"
  | "/api/tour-agencies"
  | "/api/tour-agencies/$id"
  | "/api/service-providers"
  | "/api/service-providers/$id"
  | "/api/hotels"
  | "/api/hotels/$id"
  | "/api/hotel-rooms"
  | "/api/hotel-rooms/$id"
  | "/api/tourdays"
  | "/api/tourdays/$id"
  | "/api/tour-services"
  | "/api/tour-services/$id"
  | "/api/tour-accommodations"
  | "/api/tour-accommodations/$id"
  | "/api/reservations"
  | "/api/reservations/$id"
  | "/api/clients"
  | "/api/clients/$id"
  | "/api/reservation-clients"
  | "/api/reservation-clients/$id"
  | "/api/reservation-days"
  | "/api/reservation-days/$id"
  | "/api/reservation-services"
  | "/api/reservation-services/$id"
  | "/api/accommodations"
  | "/api/accommodations/$id"
  | "/api/fulfillments"
  | "/api/fulfillments/$id"
  | "/api/departments"
  | "/api/departments/$id"
  | "/api/email-templates"
  | "/api/email-templates/$id"
  | "/api/triggers"
  | "/api/triggers/$id"
  | "/api/trigger-events";

  interface GetRequestOptions {
    filters?: Record<string, unknown>;
    projection?: string[];
    pathReplace?: Record<string, string>;
    query?: Record<string, string>;
    etag?: string; // Add etag to options
  }

interface PutRequestOptions extends Pick<GetRequestOptions, "pathReplace"> {
  etag?: string; // Add etag to DELETE options

}

interface DeleteRequestOptions extends Pick<GetRequestOptions, "pathReplace"> {
  etag?: string; // Add etag to DELETE options

}

class Api {
  
  private readonly axios = axios.create({
    baseURL: env.api.baseUrl,
    
  });

  async get<T>(
    path: paths,
    dtoSchema: ZodType<T>,
    options?: GetRequestOptions,
  ): Promise<T> {
    const response = await this.axios.patch(
      this.replaceInPath(path, options?.pathReplace),
      {
        Filters: options?.filters,
        Includes: options?.projection,
      },
      {
        params: options?.query,
        // headers: {
        //   Authorization: `Bearer ${await auth.token}`,
        // },
        headers: {
          Authorization: `Bearer ${await auth.token}`,
        },
      },
    );

    return dtoSchema.parse(response.data);
  }
  async post<TIN, TOUT>(
    path: paths,
    data: TIN,
    dtoSchema: ZodType<TOUT>,
  ): Promise<TOUT> {

    const headers: Record<string, string> = {
      Authorization: `Bearer ${await auth.token}`,
    };

    await this.ifMatchInvalidate(undefined, headers);


    const response = await this.axios.post(path, data, {
      headers,
    });

    const newEntityId = response.data.Id;
    await invalidateByEntities([newEntityId]);

    return dtoSchema.parse(response.data);
  }
  async put<TIN, TOUT>(
    path: paths,
    data: TIN,
    dtoSchema: ZodType<TOUT>,
    options?: PutRequestOptions,
  ): Promise<TOUT> {


    const headers: Record<string, string> = {
      Authorization: `Bearer ${await auth.token}`,
    };

    await this.ifMatchInvalidate(options, headers);

    const response = await this.axios.put(
      this.replaceInPath(path, options?.pathReplace),
      data,
      {
        headers,
      },
    );

    const affectedIds = [response.data.Id]; // Adjust based on response structure
    await invalidateByEntities(affectedIds);

    return dtoSchema.parse(response.data);
  }
  async delete<T>(
    path: paths,
    dtoSchema?: ZodType<T>,
    options?: DeleteRequestOptions,
  ): Promise<T | undefined> {

    const headers: Record<string, string> = {
      Authorization: `Bearer ${await auth.token}`,
    };

    await this.ifMatchInvalidate(options, headers);

    const response = await this.axios.delete(
      this.replaceInPath(path, options?.pathReplace),
      {
        headers,
      },
    );

    const deletedId = parseInt(this.replaceInPath("$id",options?.pathReplace));
    await invalidateByEntities([deletedId]);

    return dtoSchema?.parse(response.data);
  }
  async getWithCache<T extends {Data : {Id : number}[]}>(
    path: paths,
    dtoSchema: ZodType<T>,
    options?: GetRequestOptions,
  ): Promise<T> {
    const db = await getUserDb();
    const queryKey = this.generateQueryKey(path, options);
    const cachedItem = await db.table('cache').get(queryKey);
    let cachedData = "";
    if (cachedItem?.data != undefined){
      cachedData = await secureDecrypt(cachedItem.data);
    }

    try {
      const { data, etag, ids } = await api.getWithEtag(path, dtoSchema, {
        ...options,
        etag: cachedItem?.etag,
      });
  
      await db.table('cache').put({
        key: queryKey,
        entityPath: this.getBasePath(path),
        data: await secureEncrypt(JSON.stringify(data)),
        etag,
        timestamp: Date.now()
      });

      // await db.table('cache').put({
      //   key: altQueryKey,
      //   data: await secureEncrypt(JSON.stringify(data)),
      //   etag,
      //   timestamp: Date.now()
      // });

      for (const id of ids) {
        await db.table('entityCacheMap').put({
          entityId: id,
          cacheKey: queryKey
        });
      }
      
      return data;
    } catch (error) {
      if (error instanceof Error && error.message === '304 Not Modified' && cachedItem) {
        return JSON.parse(cachedData) as T;
      }
      throw error;
    }
  }
  async deleteEntity<TIN, TOUT>(
    path: paths,
    data: TIN,
    dtoSchema?: ZodType<TOUT>,
    options?: DeleteRequestOptions,
  ): Promise<TOUT | undefined> {


    const headers: Record<string, string> = {
      Authorization: `Bearer ${await auth.token}`,
    };

    await this.ifMatchInvalidate(options, headers);


    const response = await this.axios.delete(
      this.replaceInPath(path, options?.pathReplace),
      {
        headers,
        data,
      },
    );
  
    return dtoSchema?.parse(response.data);
  }
  private getBasePath(path: string): string {
    const pathParts = path.split('/');
    return pathParts.slice(0, 3).join('/');
  }
  private async ifMatchInvalidate(options: PutRequestOptions | DeleteRequestOptions | undefined, headers: Record<string, string>) {
    const etag = options?.etag;
    // if (!etag) {
    //   const db = await getUserDb();
    //   const queryKey = this.generateQueryKey(path);
    //   const cachedItem = await db.table('cache').get(queryKey);
    //   etag = cachedItem?.etag;
    //   await invalidate(path);
    // }

    if (etag) {
      headers['If-Match'] = etag;
    }
  }

  public async refreshCacheEntry(cacheKey: string): Promise<void> {
    const db = await getUserDb();
    const cachedItem = await db.table('cache').get(cacheKey);
    if (!cachedItem) return;

    const { requestPath, filters, projection, pathReplace, query, dtoSchema } = cachedItem;

    try {
      // Re-fetch fresh data
      const { data, etag, ids } = await this.getWithEtag(
        requestPath as paths,
        dtoSchema, // Retrieve schema dynamically (see notes)
        { filters, projection, pathReplace, query }
      );

      // Update cache
      await db.table('cache').put({
        ...cachedItem,
        data: await secureEncrypt(JSON.stringify(data)),
        etag,
        timestamp: Date.now(),
        ids,
      });

      // Update entity mappings
      await db.table('entityCacheMap').where('cacheKey').equals(cacheKey).delete();
      for (const id of ids) {
        await db.table('entityCacheMap').put({ entityId: id, cacheKey });
      }
    } catch (error) {
      console.error(`Refresh failed for ${cacheKey}:`, error);
      await db.table('cache').delete(cacheKey); // Remove if unrecoverable
    }
  }

  private replaceInPath(path: string, replacements?: Record<string, string>) {
    const entries = Object.entries(replacements ?? {});
    return entries.length
      ? entries.reduce<string>(
          (acc, [key, value]) => acc.replace(key, value),
          path,
        )
      : path;
  }
  private generateQueryKey(path: paths | string, options?: GetRequestOptions): string {
    const { filters, projection, pathReplace, query } = options || {};
    return `${path}|${JSON.stringify({filters, projection, pathReplace, query})}`;
  }
  private async getWithEtag<T extends {Data : {Id : number}[]}>(
    path: paths,
    dtoSchema: ZodType<T>,
    options?: GetRequestOptions,
  ): Promise<{ data: T; etag: string, ids: number[] }> {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${await auth.token}`,
    };

    if (options?.etag) {
      headers['If-None-Match'] = options.etag;
    }

    const response = await this.axios.patch<T>(
      this.replaceInPath(path, options?.pathReplace),
      {
        Filters: options?.filters,
        Includes: options?.projection,
      },
      {
        params: options?.query,
        headers,
        validateStatus: (status) => status === 200 || status === 304,
      },
    );

    if (response.status === 304) {
      throw new Error('304 Not Modified');
    }

    const data = dtoSchema.parse(response.data);
    const etag = response.headers['etag'];
    const ids = response.data.Data.map(x => x.Id);
    return { data, etag, ids };
  }
}

const globalForApi = globalThis as typeof globalThis & { api: Api };

export const api = globalForApi.api ?? new Api();

if (env.mode === "production") {
  globalForApi.api = api;
}